import { Activity, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const LiveStatsSection = ({ formData, setFormData }) => {
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  const validateStatsUrl = async () => {
    if (!formData.stats_link) {
      setValidationResult({ valid: false, error: "No URL provided" });
      return;
    }
    
    setValidating(true);
    setValidationResult(null);
    
    try {
      const res = await axios.post(`${API}/livestats/validate-stats-url?url=${encodeURIComponent(formData.stats_link)}`);
      setValidationResult(res.data);
    } catch (err) {
      setValidationResult({ valid: false, error: err.response?.data?.detail || "Validation failed" });
    } finally {
      setValidating(false);
    }
  };
  
  return (
    <div className="p-4 bg-zinc-800/50 border border-white/5">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-green-500" />
        <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400">Live Stats Feed</h4>
      </div>
      
      {/* Provider Selection */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setFormData({ ...formData, stats_provider: 'prestosports' })}
          className={`px-3 py-1.5 text-xs font-bold uppercase ${
            formData.stats_provider === 'prestosports' 
              ? 'bg-green-600 text-white' 
              : 'bg-zinc-800 text-zinc-500 hover:text-white'
          }`}
        >
          PrestoSports
        </button>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, stats_provider: 'sidearm' })}
          className={`px-3 py-1.5 text-xs font-bold uppercase ${
            formData.stats_provider === 'sidearm' 
              ? 'bg-blue-600 text-white' 
              : 'bg-zinc-800 text-zinc-500 hover:text-white'
          }`}
        >
          Sidearm Sports
        </button>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, stats_provider: 'statmoose' })}
          className={`px-3 py-1.5 text-xs font-bold uppercase ${
            formData.stats_provider === 'statmoose' 
              ? 'bg-orange-600 text-white' 
              : 'bg-zinc-800 text-zinc-500 hover:text-white'
          }`}
        >
          StatMoose
        </button>
      </div>
      
      {/* Stats Link Input with Validation */}
      <div className="flex gap-2">
        <input 
          type="text" 
          value={formData.stats_link} 
          onChange={(e) => {
            setFormData({ ...formData, stats_link: e.target.value });
            setValidationResult(null);
          }} 
          placeholder={
            formData.stats_provider === 'prestosports' 
              ? "https://naiastats.prestosports.com/sports/mbkb/2025-26/boxscores/..." 
              : formData.stats_provider === 'sidearm'
              ? "https://[school]sports.com/sidearmstats/mbball/summary"
              : "https://statmoose.com/media/football/XXXXXXXX or share code"
          }
          className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
          data-testid="stats-link-input"
        />
        <button
          type="button"
          onClick={validateStatsUrl}
          disabled={validating || !formData.stats_link}
          className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {validating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Validate"
          )}
        </button>
      </div>
      
      {/* Validation Result */}
      {validationResult && (
        <div className={`mt-3 p-3 rounded text-sm ${
          validationResult.valid 
            ? 'bg-green-500/20 border border-green-500/50' 
            : 'bg-red-500/20 border border-red-500/50'
        }`}>
          <div className="flex items-start gap-2">
            {validationResult.valid ? (
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            )}
            <div>
              {validationResult.valid ? (
                <>
                  <p className="text-green-400 font-bold">URL Valid</p>
                  <p className="text-zinc-300 text-xs mt-1">
                    Provider: <span className="text-white">{validationResult.provider}</span>
                  </p>
                  {validationResult.teams && (
                    <p className="text-zinc-300 text-xs">
                      Teams: <span className="text-white">{validationResult.teams.away}</span> @ <span className="text-white">{validationResult.teams.home}</span>
                      {validationResult.teams.away_score > 0 || validationResult.teams.home_score > 0 ? (
                        <span className="text-orange-400 ml-2">
                          ({validationResult.teams.away_score} - {validationResult.teams.home_score})
                        </span>
                      ) : null}
                    </p>
                  )}
                  <div className="flex gap-4 mt-2 text-xs">
                    <span className={validationResult.has_boxscore ? 'text-green-400' : 'text-zinc-500'}>
                      {validationResult.has_boxscore ? '✓' : '✗'} Box Score
                      {validationResult.player_count > 0 && ` (${validationResult.player_count} players)`}
                    </span>
                    <span className={validationResult.has_playbyplay ? 'text-green-400' : 'text-zinc-500'}>
                      {validationResult.has_playbyplay ? '✓' : '✗'} Play-by-Play
                      {validationResult.play_count > 0 && ` (${validationResult.play_count} plays)`}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-red-400 font-bold">Validation Failed</p>
                  <p className="text-zinc-300 text-xs mt-1">{validationResult.error}</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="mt-3 p-3 bg-black/30 border border-white/5">
        <p className="text-xs text-zinc-400 mb-2">
          <strong className="text-white">Data Available by Provider:</strong>
        </p>
        <div className="grid grid-cols-3 gap-2 text-[10px]">
          <div>
            <span className="text-green-500 font-bold">PrestoSports</span>
            <ul className="text-zinc-500 mt-1 space-y-0.5">
              <li>✓ Live Score</li>
              <li>✓ Team Stats</li>
              <li>✓ Player Stats</li>
              <li>✓ Play-by-Play</li>
            </ul>
          </div>
          <div>
            <span className="text-blue-500 font-bold">Sidearm Sports</span>
            <ul className="text-zinc-500 mt-1 space-y-0.5">
              <li>✓ Live Score</li>
              <li>✗ Team Stats</li>
              <li>✗ Player Stats</li>
              <li>✗ Player of Game</li>
            </ul>
          </div>
          <div>
            <span className="text-orange-500 font-bold">StatMoose</span>
            <ul className="text-zinc-500 mt-1 space-y-0.5">
              <li>✓ Live Score</li>
              <li>✓ Team Stats</li>
              <li>✓ Player Stats</li>
              <li>✓ Play-by-Play</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* GameCast Detection */}
      {formData.stats_link && (formData.stats_link.includes('statmoose') || formData.stats_link.includes('stat-go-app')) && (
        <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded flex items-center gap-2">
          <Activity className="w-4 h-4 text-orange-500" />
          <div>
            <p className="text-orange-400 text-xs font-bold">StatMoose GameCast Enabled</p>
            <p className="text-zinc-500 text-[10px]">ESPN-style scoreboard, box score, play-by-play, and team stats will appear on the game page automatically.</p>
          </div>
        </div>
      )}
      
      {/* Overlay Toggle */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
        <div>
          <label className="text-sm text-white font-bold">Show Score Overlay</label>
          <p className="text-xs text-zinc-500">Display live score bug on video player</p>
        </div>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, show_stats_overlay: !formData.show_stats_overlay })}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            formData.show_stats_overlay ? 'bg-green-600' : 'bg-zinc-700'
          }`}
          data-testid="overlay-toggle"
        >
          <span 
            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              formData.show_stats_overlay ? 'left-7' : 'left-1'
            }`}
          />
        </button>
      </div>

      {/* ScoreBird Live Scoreboard Link */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-yellow-500">
            <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
          </svg>
          <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400">ScoreBird Live Scoreboard</h4>
        </div>
        <input
          type="text"
          value={formData.scorebird_link || ''}
          onChange={(e) => setFormData({ ...formData, scorebird_link: e.target.value })}
          placeholder="https://nestcloud-api.scorebird.com/v2/game-state/vmix?nest_id=...&api_key=..."
          className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
          data-testid="scorebird-link-input"
        />
        <p className="text-xs text-zinc-600 mt-1">
          Paste the full ScoreBird NestCloud API URL for live clock, score, down & distance from the physical scoreboard
        </p>
      </div>

      {/* MaxPreps section removed - inputs now in TeamFormSection */}
    </div>
  );
};